/* eslint-disable no-console */

import useTranslations from '~/components/translation/use-translations';
import { isTextKey } from '../my-notifications-utils';
import { useEffect, useState } from 'react';

const usePreprocess = notification => {
	const [data, setData] = useState(notification);
	const retNotification = structuredClone(notification);
	const { ctaText, ctaUrl, image, title, subtitle, description, optixRecommendationType } = retNotification;
	const textkeys = [];

	// Only to be used for Rich Text Editor
	const addTextKeysFromEditorState = sampleData => {
		if (sampleData) {
			try {
				// If we can parse the data, the data is already in object form
				const parsedData = JSON.parse(sampleData);
				if (parsedData?.text && parsedData?.text.trim() !== '') {
					// NOT RICH TEXT
					if (isTextKey(parsedData.text)) {
						textkeys.push(parsedData.text);
					}
				}
			} catch (e) {
				// If we cant parse the data, the data is still in the old format,
				if (sampleData && isTextKey(sampleData)) {
					textkeys.push(sampleData);
				}
			}
		}
	};

	// To be used for all other texts
	const addIfTextKey = text => {
		if (isTextKey(text)) textkeys.push(text);
	};

	addIfTextKey(ctaText);
	addTextKeysFromEditorState(title);
	addTextKeysFromEditorState(subtitle);
	addTextKeysFromEditorState(description);

	if (retNotification?.data) {
		try {
			// retNotification.data = JSON.parse(retNotification.data);
			const { Accordion } = retNotification.data;
			if (Accordion) {
				// eslint-disable-next-line camelcase
				const { accordion_title, accordion_content } = Accordion;
				addTextKeysFromEditorState(accordion_title);
				addTextKeysFromEditorState(accordion_content);
			}
		} catch (e) {
			console.error('Data parse error on notification id: #%d', retNotification.id);
		}
	}

	if (retNotification?.notificationPopUp) {
		const { title: popupTitle, subtitle: popupSubtitle, ctaText: popupCtaText } = retNotification.notificationPopUp;

		addTextKeysFromEditorState(popupTitle);
		addTextKeysFromEditorState(popupSubtitle);
		addIfTextKey(popupCtaText);

		if (retNotification.notificationPopUp?.data) {
			try {
				retNotification.notificationPopUp.data = JSON.parse(retNotification.notificationPopUp.data);
				const { PopupAdditionalData } = retNotification.notificationPopUp.data;
				if (PopupAdditionalData) {
					const { title: popupAdditionalDataTitle, content: popupAdditionalDataContent } =
						PopupAdditionalData;

					addTextKeysFromEditorState(popupAdditionalDataTitle);
					addTextKeysFromEditorState(popupAdditionalDataContent);
				}
			} catch (e) {
				console.error('Data parse error on notification popup id: #%d', retNotification.notificationPopUp.id);
			}
		}
	}

	const { translations, isLoadingTranslations } = useTranslations(textkeys);

	const placeholders = Array.from(Object.entries(retNotification?.personalizationData || {}));
	// currently only using the first element in the recommendations array
	// /%@optixCasinoGameCode[0-9]+%/gm regex can be used to match placeholders with index accessors
	// e.g. %@optixCasinoGameCode0%
	// replace 2nd element in the placeholders array with a function
	// to access and return the correct index element in the future
	if (optixRecommendationType === 'casino') {
		const optixCasinoRecommendations = retNotification.personalizationData?.recommendations[0].result;
		if (optixCasinoRecommendations.length > 0) {
			const optixCasinoPlaceholders = [
				['optixCasinoUrlShortcutVerb', optixCasinoRecommendations[0]?.extra?.urlShortcutVerb],
				['optixCasinoImageUrl', optixCasinoRecommendations[0].imageURL],
				['optixCasinoName', optixCasinoRecommendations[0].name]
			];
			placeholders.push(...optixCasinoPlaceholders);
		}
	}
	// currently only using the first element in the recommendations array
	if (optixRecommendationType === 'sportsbook') {
		const optixSportsbookRecommendations = retNotification.personalizationData?.recommendations[0].result;
		if (optixSportsbookRecommendations.length > 0) {
			const optixSportsbookPlaceholders = [
				['optixSportsbookEventName', optixSportsbookRecommendations[0].eventName],
				['optixSportsbookEventDateTime', optixSportsbookRecommendations[0].eventDateTime],
				['optixSportsbookDecPrice', optixSportsbookRecommendations[0].decPrice],
				['optixSportsbookMinsToOff', optixSportsbookRecommendations[0].minsToOff],
				['optixSportsbookEventKey', optixSportsbookRecommendations[0].eventKey],
				['optixSportsbookSelectionKey', optixSportsbookRecommendations[0].selectionKey],
				['optixSportsbookSelectionName', optixSportsbookRecommendations[0].selectionName]
			];
			placeholders.push(...optixSportsbookPlaceholders);
		}
	}

	useEffect(() => {
		// Replace placeholders and textkeys for NORMAL TEXT
		const replacePlaceholders = text => {
			if (!text) {
				return text;
			}
			let ret = isTextKey(text) ? translations[text] : text;

			if (placeholders.length === 0) {
				return ret;
			}

			placeholders.forEach(placeholder => {
				// replace the placeholder with the corresponding value
				// ONLY IF there is a value
				if (placeholder[1]) {
					ret = ret.replaceAll(`%@${placeholder[0]}%`, placeholder[1]);
				}
			});

			return ret;
		};

		// Replace placeholders and textkeys for RICH TEXT
		const replacePlaceholdersRichText = editorData => {
			if (!editorData) {
				return editorData;
			}

			let updatedEditorData;

			try {
				// If we can parse the data, the data is already in object form
				const parsedData = JSON.parse(editorData);
				updatedEditorData = { ...parsedData };

				if (parsedData?.text && parsedData?.text.trim() !== '') {
					// NOT RICH TEXT
					updatedEditorData.text = isTextKey(parsedData.text)
						? translations[parsedData.text]
						: parsedData.text;

					if (placeholders.length === 0) {
						return JSON.stringify(updatedEditorData);
					}

					placeholders.forEach(placeholder => {
						// replace the placeholder with the corresponding value
						// ONLY IF there is a value
						if (placeholder[1]) {
							updatedEditorData.text = updatedEditorData.text.replaceAll(
								`%@${placeholder[0]}%`,
								placeholder[1]
							);
						}
					});
				} else {
					// IS RICH TEXT
					// PS: CAN'T BE A TEXT KEY
					updatedEditorData.editorState = parsedData.editorState;

					if (placeholders.length === 0) {
						return JSON.stringify(updatedEditorData);
					}

					placeholders.forEach(placeholder => {
						// replace the placeholder with the corresponding value
						// ONLY IF there is a value
						if (placeholder[1]) {
							updatedEditorData.editorState = updatedEditorData.editorState.replaceAll(
								`%@${placeholder[0]}%`,
								placeholder[1]
							);
						}
					});
				}
				return JSON.stringify(updatedEditorData);
			} catch (e) {
				// If we cant parse the data, the data is still in the old format,
				updatedEditorData = isTextKey(editorData) ? translations[editorData] : editorData;

				if (placeholders.length === 0) {
					return updatedEditorData;
				}

				placeholders.forEach(placeholder => {
					// replace the placeholder with the corresponding value
					// ONLY IF there is a value
					if (placeholder[1]) {
						updatedEditorData = updatedEditorData.replaceAll(`%@${placeholder[0]}%`, placeholder[1]);
					}
				});

				return updatedEditorData;
			}
		};

		if (!isLoadingTranslations) {
			retNotification.ctaText = replacePlaceholders(ctaText);
			retNotification.ctaUrl = replacePlaceholders(ctaUrl);
			retNotification.image = replacePlaceholders(image);
			retNotification.title = replacePlaceholdersRichText(title);
			retNotification.subtitle = replacePlaceholdersRichText(subtitle);
			retNotification.description = replacePlaceholdersRichText(description);

			if (retNotification?.data?.Accordion) {
				// eslint-disable-next-line camelcase
				const { accordion_title, accordion_content } = retNotification.data.Accordion;

				retNotification.data.Accordion.accordion_title = replacePlaceholdersRichText(accordion_title);
				retNotification.data.Accordion.accordion_content = replacePlaceholdersRichText(accordion_content);
			}

			if (retNotification?.notificationPopUp) {
				const { notificationPopUp } = retNotification;
				const {
					title: popupTitle,
					subtitle: popupSubtitle,
					image: popupImage,
					ctaText: popupCtaText,
					ctaUrl: popupCtaUrl
				} = notificationPopUp;

				notificationPopUp.title = replacePlaceholdersRichText(popupTitle);
				notificationPopUp.subtitle = replacePlaceholdersRichText(popupSubtitle);
				notificationPopUp.image = replacePlaceholders(popupImage);
				notificationPopUp.ctaText = replacePlaceholders(popupCtaText);
				notificationPopUp.ctaUrl = replacePlaceholders(popupCtaUrl);

				if (notificationPopUp?.data?.PopupAdditionalData) {
					const { PopupAdditionalData } = retNotification.notificationPopUp.data;
					const { title: popupAdditionalDataTitle, content: popupAdditionalDataContent } =
						PopupAdditionalData;
					PopupAdditionalData.title = replacePlaceholdersRichText(popupAdditionalDataTitle);
					PopupAdditionalData.content = replacePlaceholdersRichText(popupAdditionalDataContent);
				}
			}
			setData(retNotification);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingTranslations]);

	return data;
};

export default usePreprocess;
